'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { RefCallback } from 'react';
import { useState } from 'react';
import { BackerRewardsHowItWorksModal } from '@/components/how-it-works/backer-rewards-how-it-works-modal';
import { OptimizedImage } from '@/components/images/optimized-image';
import { PortfolioPanelHeader } from '@/components/portfolio/shared/portfolio-panel-header';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Tick02Icon } from '@/components/ui/icon/glyphs';
import { InfiniteList } from '@/components/ui/infinite-list';
import { Panel, PanelContent } from '@/components/ui/panel';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { useMarketDetailPrefetchIntent } from '@/lib/sdk/prefetch';
import { cn } from '@/lib/ui/utils';

/** Per-market view-row consumed by the card. All money pre-decoded to display units. */
export interface BackerRewardsRowView {
  marketPubkey: string;
  marketTitle: string | null;
  marketImageUrl: string | null;
  marketState: 'created' | 'backing' | 'trading' | 'resolved' | 'graduated';
  /** User's backing principal in display units (share tokens are minted 1:1 with USDC backed). */
  backedAmount: number;
  /** Fraction of the fee vault, 0..1 — `backedD6 / backedTotalD6`, stable across claims. */
  vaultSharePct: number;
  /** Pro-rata fees earned in display units. */
  earnedAmount: number;
  /** Lifetime earnings — live earned plus already-claimed — for the Earned
   *  column, so a claimed row doesn't read as $0 (MOR-1835). */
  lifetimeEarnedAmount: number;
  /** Action discriminant — exactly one per row. */
  action: 'claim' | 'claimed' | 'accruing';
  /** True while THIS row's claim tx is in flight. */
  isClaiming: boolean;
}
export interface BackerRewardsCardViewProps {
  action?: React.ReactNode;
  /** Lifetime earned across every backed market (not only loaded rows), display units. */
  totalEarned: number;
  /** Σ earnedD6 where canClaim across every backed market, display units. */
  claimableNow: number;
  /** Count of every backed market, including rows on unloaded pages. */
  marketsBacked: number;
  rows: readonly BackerRewardsRowView[];
  /** Fire the claim for a market — only invoked on the viewer's own portfolio. */
  onClaim: (marketPubkey: string) => void;
  /** When false (viewing another user), the Claim button is hidden — the claim is theirs to take, not the viewer's. */
  canClaimHere: boolean;
  /**
   * Per-row viewport-ref factory from the SDK list hook (`query.itemObserver`).
   * Attaching it to each row's element registers that market's bounded NATS
   * subjects (`morfi.market.<pubkey>.user.*.trade.v0` etc.) while the row is on
   * screen, so a trade on a backed market patches the row's `earnedD6` live —
   * which the parent re-sums into the tiles.
   */
  getRowRef: (marketPubkey: string) => RefCallback<Element>;
  /** Cursor-pagination state for the scrolling row list. */
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void | Promise<void>;
  className?: string | undefined;
}

/**
 * Backer rewards portfolio card — presentational leaf. Renders the "not counted
 * in PnL" panel: three summary tiles (Total earned · Claimable now · Markets
 * backed) over one row per backed market, each with a state-driven action
 * (Claim / Claimed / Accruing). Backer rewards are a SEPARATE earnings stream —
 * this card never feeds any PnL total.
 */
export function BackerRewardsCardView(t0) {
  const $ = _c(64);
  const {
    action,
    totalEarned,
    claimableNow,
    marketsBacked,
    rows,
    onClaim,
    canClaimHere,
    getRowRef,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    className
  } = t0;
  const t = useTranslations("portfolio.backerRewards");
  const tHowItWorks = useTranslations("backer_rewards_how_it_works");
  const {
    formatBalance
  } = useFormattedCurrency();
  const [helpOpen, setHelpOpen] = useState(false);
  let t1;
  if ($[0] !== tHowItWorks) {
    t1 = tHowItWorks("title");
    $[0] = tHowItWorks;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setHelpOpen(true);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== helpOpen || $[4] !== t1) {
    t3 = {
      label: t1,
      onClick: t2,
      expanded: helpOpen
    };
    $[3] = helpOpen;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const infoTrigger = t3;
  let t4;
  if ($[6] !== className) {
    t4 = cn("min-w-0", className);
    $[6] = className;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("title");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== action || $[11] !== infoTrigger || $[12] !== t5) {
    t6 = <PortfolioPanelHeader title={t5} info={infoTrigger} action={action} />;
    $[10] = action;
    $[11] = infoTrigger;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("totalEarned");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== formatBalance || $[17] !== totalEarned) {
    t8 = <AnimatedNumber value={totalEarned} format={formatBalance} animateOnMount={false} className="text-heading-md font-bold tabular-nums text-feedback-success-content" />;
    $[16] = formatBalance;
    $[17] = totalEarned;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <SummaryTile label={t7} tone="success" testId="backer-rewards-total-earned">{t8}</SummaryTile>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t) {
    t10 = t("claimableNow");
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== claimableNow || $[25] !== formatBalance) {
    t11 = <AnimatedNumber value={claimableNow} format={formatBalance} animateOnMount={false} className="text-heading-md font-bold tabular-nums text-foreground-primary" />;
    $[24] = claimableNow;
    $[25] = formatBalance;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11) {
    t12 = <SummaryTile label={t10} testId="backer-rewards-claimable-now">{t11}</SummaryTile>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t) {
    t13 = t("marketsBacked");
    $[30] = t;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== marketsBacked) {
    t14 = <span className="text-heading-md font-bold tabular-nums text-foreground-primary">{marketsBacked}</span>;
    $[32] = marketsBacked;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t13 || $[35] !== t14) {
    t15 = <SummaryTile label={t13} testId="backer-rewards-markets-backed">{t14}</SummaryTile>;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t12 || $[38] !== t15 || $[39] !== t9) {
    t16 = <div className="grid grid-cols-3 gap-3" data-testid="backer-rewards-summary">{t9}{t12}{t15}</div>;
    $[37] = t12;
    $[38] = t15;
    $[39] = t9;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== canClaimHere || $[42] !== formatBalance || $[43] !== getRowRef || $[44] !== onClaim) {
    t17 = row_0 => <BackerRewardsRow row={row_0} onClaim={onClaim} canClaimHere={canClaimHere} formatBalance={formatBalance} rowRef={getRowRef(row_0.marketPubkey)} />;
    $[41] = canClaimHere;
    $[42] = formatBalance;
    $[43] = getRowRef;
    $[44] = onClaim;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== fetchNextPage || $[47] !== hasNextPage || $[48] !== isFetchingNextPage || $[49] !== rows || $[50] !== t17) {
    t18 = <InfiniteList items={rows} getItemKey={_temp} renderItem={t17} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage} listClassName="flex flex-col gap-2.5" />;
    $[46] = fetchNextPage;
    $[47] = hasNextPage;
    $[48] = isFetchingNextPage;
    $[49] = rows;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== t16 || $[53] !== t18 || $[54] !== t6) {
    t19 = <PanelContent className="flex flex-col gap-4">{t6}{t16}{t18}</PanelContent>;
    $[52] = t16;
    $[53] = t18;
    $[54] = t6;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== t19 || $[57] !== t4) {
    t20 = <Panel className={t4}>{t19}</Panel>;
    $[56] = t19;
    $[57] = t4;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== helpOpen) {
    t21 = <BackerRewardsHowItWorksModal open={helpOpen} onOpenChange={setHelpOpen} showEstimateNote={false} />;
    $[59] = helpOpen;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== t20 || $[62] !== t21) {
    t22 = <>{t20}{t21}</>;
    $[61] = t20;
    $[62] = t21;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  return t22;
}

// ---------------------------------------------------------------------------
// Summary tile — one bordered inset chip (Total earned · Claimable now · …).
// ---------------------------------------------------------------------------
function _temp(row) {
  return row.marketPubkey;
}
function SummaryTile(t0) {
  const $ = _c(11);
  const {
    label,
    tone: t1,
    testId,
    children
  } = t0;
  const tone = t1 === undefined ? "neutral" : t1;
  let t2;
  if ($[0] !== label) {
    t2 = <span className="text-caption text-foreground-tertiary">{label}</span>;
    $[0] = label;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = tone === "success" && "text-feedback-success-content";
  let t4;
  if ($[2] !== t3) {
    t4 = cn(t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== children || $[5] !== t4 || $[6] !== testId) {
    t5 = <span className={t4} data-testid={testId}>{children}</span>;
    $[4] = children;
    $[5] = t4;
    $[6] = testId;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t2 || $[9] !== t5) {
    t6 = <div className="flex flex-col gap-1 rounded-panel-inset border p-3">{t2}{t5}</div>;
    $[8] = t2;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}

// ---------------------------------------------------------------------------
// Per-market row — thumb · title + subtitle · Earned · state-driven action.
// ---------------------------------------------------------------------------

const PCT_FORMATTER: Intl.NumberFormat = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});
function BackerRewardsRow(t0) {
  const $ = _c(52);
  const {
    row,
    onClaim,
    canClaimHere,
    formatBalance,
    rowRef
  } = t0;
  const t = useTranslations("portfolio.backerRewards");
  let t1;
  if ($[0] !== row.marketTitle || $[1] !== t) {
    t1 = row.marketTitle != null && row.marketTitle.length > 0 ? row.marketTitle : t("unknownMarket");
    $[0] = row.marketTitle;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const displayTitle = t1;
  const isProposal = row.marketState === "created" || row.marketState === "backing";
  const marketHref = isProposal ? `/proposals/${row.marketPubkey}` : `/markets/${row.marketPubkey}`;
  const dataWarm = useMarketDetailPrefetchIntent(isProposal ? null : row.marketPubkey);
  const intent = useIntentRoutePrefetch(marketHref, dataWarm.onMouseEnter);
  const t2 = row.marketPubkey;
  let t3;
  if ($[3] !== displayTitle || $[4] !== intent || $[5] !== marketHref) {
    t3 = <Link href={marketHref} prefetch={false} aria-label={displayTitle} className="absolute inset-0 z-0 rounded-panel-row" {...intent} />;
    $[3] = displayTitle;
    $[4] = intent;
    $[5] = marketHref;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== row.marketImageUrl || $[8] !== row.marketPubkey) {
    t4 = <OptimizedImage variant="thumbnail" entity={{
      kind: "market",
      pubkey: row.marketPubkey,
      category: "",
      coverImageUrl: row.marketImageUrl,
      nsfw: false
    }} alt="" size="size-10" rounded="rounded-control" preview={false} />;
    $[7] = row.marketImageUrl;
    $[8] = row.marketPubkey;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== marketHref || $[11] !== t4) {
    t5 = <Link href={marketHref} prefetch={false} className="relative z-10 shrink-0">{t4}</Link>;
    $[10] = marketHref;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== displayTitle || $[14] !== marketHref) {
    t6 = <Link href={marketHref} prefetch={false} className="relative z-10 truncate text-body font-medium text-foreground-primary">{displayTitle}</Link>;
    $[13] = displayTitle;
    $[14] = marketHref;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== formatBalance || $[17] !== row.backedAmount || $[18] !== row.vaultSharePct || $[19] !== t) {
    t7 = t("backedSubtitle", {
      backed: formatBalance(row.backedAmount),
      pct: PCT_FORMATTER.format(row.vaultSharePct * 100)
    });
    $[16] = formatBalance;
    $[17] = row.backedAmount;
    $[18] = row.vaultSharePct;
    $[19] = t;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t7) {
    t8 = <span className="truncate text-caption text-foreground-tertiary">{t7}</span>;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t6 || $[24] !== t8) {
    t9 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t6}{t8}</div>;
    $[23] = t6;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t) {
    t10 = t("earnedLabel");
    $[26] = t;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10) {
    t11 = <span className="text-caption text-foreground-tertiary">{t10}</span>;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const t12 = row.lifetimeEarnedAmount;
  const t13 = row.lifetimeEarnedAmount > 0 ? "text-feedback-success-content" : "text-foreground-primary";
  let t14;
  if ($[30] !== t13) {
    t14 = cn("text-body font-semibold tabular-nums", t13);
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== formatBalance || $[33] !== row.lifetimeEarnedAmount || $[34] !== t14) {
    t15 = <AnimatedNumber value={t12} format={formatBalance} animateOnMount={false} data-testid="backer-rewards-row-earned" className={t14} />;
    $[32] = formatBalance;
    $[33] = row.lifetimeEarnedAmount;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t11 || $[37] !== t15) {
    t16 = <div className="flex shrink-0 flex-col items-end">{t11}{t15}</div>;
    $[36] = t11;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== canClaimHere || $[40] !== formatBalance || $[41] !== onClaim || $[42] !== row) {
    t17 = <RowAction row={row} onClaim={onClaim} canClaimHere={canClaimHere} formatBalance={formatBalance} />;
    $[39] = canClaimHere;
    $[40] = formatBalance;
    $[41] = onClaim;
    $[42] = row;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== row.marketPubkey || $[45] !== rowRef || $[46] !== t16 || $[47] !== t17 || $[48] !== t3 || $[49] !== t5 || $[50] !== t9) {
    t18 = <div ref={rowRef} className="relative flex items-center gap-3.5 rounded-panel-row border p-3.5 transition-colors hover:bg-background-tertiary" data-testid="backer-rewards-row" data-market={t2}>{t3}{t5}{t9}{t16}{t17}</div>;
    $[44] = row.marketPubkey;
    $[45] = rowRef;
    $[46] = t16;
    $[47] = t17;
    $[48] = t3;
    $[49] = t5;
    $[50] = t9;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  return t18;
}
function RowAction(t0) {
  const $ = _c(27);
  const {
    row,
    onClaim,
    canClaimHere,
    formatBalance
  } = t0;
  const t = useTranslations("portfolio.backerRewards");
  if (!canClaimHere) {
    return null;
  }
  if (row.action === "claim") {
    let t1;
    if ($[0] !== onClaim || $[1] !== row.marketPubkey) {
      t1 = () => onClaim(row.marketPubkey);
      $[0] = onClaim;
      $[1] = row.marketPubkey;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== formatBalance || $[4] !== row.earnedAmount || $[5] !== row.isClaiming || $[6] !== t) {
      t2 = row.isClaiming ? t("claiming") : t("claimAmount", {
        amount: formatBalance(row.earnedAmount)
      });
      $[3] = formatBalance;
      $[4] = row.earnedAmount;
      $[5] = row.isClaiming;
      $[6] = t;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== row.isClaiming || $[9] !== t1 || $[10] !== t2) {
      t3 = <AnimatedButton variant="primary" size="sm" className="relative z-10 shrink-0" onClick={t1} disabled={row.isClaiming} loading={row.isClaiming}>{t2}</AnimatedButton>;
      $[8] = row.isClaiming;
      $[9] = t1;
      $[10] = t2;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  }
  if (row.action === "claimed") {
    let t1;
    if ($[12] !== t) {
      t1 = t("claimed");
      $[12] = t;
      $[13] = t1;
    } else {
      t1 = $[13];
    }
    let t2;
    if ($[14] !== t1) {
      t2 = <AnimatedButton variant="secondary" size="sm" className="relative z-10 shrink-0" prefix={Tick02Icon} disabled={true}>{t1}</AnimatedButton>;
      $[14] = t1;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    return t2;
  }
  let t1;
  if ($[16] !== t) {
    t1 = t("claim");
    $[16] = t;
    $[17] = t1;
  } else {
    t1 = $[17];
  }
  let t2;
  if ($[18] !== t1) {
    t2 = <AnimatedButton variant="primary" size="sm" disabled={true}>{t1}</AnimatedButton>;
    $[18] = t1;
    $[19] = t2;
  } else {
    t2 = $[19];
  }
  let t3;
  if ($[20] !== t) {
    t3 = t("claimAtResolution");
    $[20] = t;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== t3) {
    t4 = <span className="text-caption text-foreground-tertiary">{t3}</span>;
    $[22] = t3;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  let t5;
  if ($[24] !== t2 || $[25] !== t4) {
    t5 = <div className="relative z-10 flex shrink-0 flex-col items-end gap-1">{t2}{t4}</div>;
    $[24] = t2;
    $[25] = t4;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  return t5;
}